import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/Steps/AggregatedFlow/mapCollectedFieldsToTrackersSetupPayload";
/**
 * 将 SaveGoal 入参映射为 ⑧ `TrackersSetupOnboardingModalContent` 所需的 payload。
 *
 * Goal 卡复用 `SaveGoalCardGoalPreview`（与 Chat `SaveGoalCard` 同源）。
 * 静态壳文案使用 `~...~` 包裹，供后续翻译扫描。
 */

import { normalizeSaveGoalTrackerSpecs } from "@/src/components/Chat/InteractiveTool/saveGoalTrackerSpecs";
import type {
  SaveGoalArgs,
  SaveGoalTrackerSpec,
} from "@/src/components/Chat/InteractiveTool/types";
import type { TrackersSetupModalPayload } from "../ResultStep/types";
import type { AggregatedOnboardingParsedInput } from "./parseAggregatedOnboardingInput";
import { TrackerSpec } from "@api/ChatApi";

const I18n = I18nUtil.createScoped(Translations);

function buildSaveGoalArgsFromCollectedFields(
  input: AggregatedOnboardingParsedInput,
  raw?: Record<string, unknown>,
): SaveGoalArgs {
  const title = input.title?.trim() ?? "";
  const summary =
    input.summary?.trim() ||
    title ||
    I18n.t("These run quietly. I only ping you when something matters.");
  const trackerSpecs = Array.isArray(input.trackerSpecs)
    ? (input.trackerSpecs as TrackerSpec[])
    : undefined;
  const memoryHitNote =
    raw && typeof raw.memoryHitNote === "string"
      ? raw.memoryHitNote
      : undefined;

  return {
    title: title || summary,
    summary,
    hasBoundPortfolio: input.hasBoundPortfolio,
    trackerSpecs,
    memoryHitNote,
    goalId: raw && typeof raw.goalId === "string" ? raw.goalId : undefined,
    confirmLabel:
      raw && typeof raw.confirmLabel === "string"
        ? raw.confirmLabel
        : undefined,
    rejectLabel:
      raw && typeof raw.rejectLabel === "string" ? raw.rejectLabel : undefined,
    targetValue:
      raw && typeof raw.targetValue === "number" ? raw.targetValue : undefined,
    expireTime:
      raw && typeof raw.expireTime === "number" ? raw.expireTime : undefined,
    accountIds:
      raw && Array.isArray(raw.accountIds)
        ? (raw.accountIds as string[])
        : undefined,
    metrics: raw?.metrics,
    coreMetrics:
      raw && Array.isArray(raw.coreMetrics)
        ? (raw.coreMetrics as SaveGoalArgs["coreMetrics"])
        : undefined,
  };
}

export const mapCollectedFieldsToTrackersSetupPayload = (
  input: AggregatedOnboardingParsedInput,
  raw?: Record<string, unknown>,
): TrackersSetupModalPayload => {
  const trackerSpecs = Array.isArray(input.trackerSpecs)
    ? (input.trackerSpecs as SaveGoalTrackerSpec[])
    : undefined;
  const displayItems = normalizeSaveGoalTrackerSpecs(trackerSpecs);
  const hasSaveHandler = Boolean(input.handleSubmitGoal);

  return {
    headerTitle: I18n.t("Meet Ed"),
    headline: I18n.t("Here's what I'll watch for you."),
    subhead: I18n.t(
      "These run quietly. I only ping you when something matters.",
    ),
    saveGoalArgs: buildSaveGoalArgsFromCollectedFields(input, raw),
    trackersSectionTitle: I18n.t("Trackers I'll run"),
    trackers: displayItems.map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      status: "active" as const,
    })),
    ctaLabelH5: hasSaveHandler ? I18n.t("Save this setup") : I18n.t("Confirm"),
    ctaLabelPc: hasSaveHandler ? I18n.t("Save this Setup") : I18n.t("Confirm"),
  };
};
